import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Users, ArrowLeft, Lock, Globe, Crown, Send, Heart,
  MessageSquare, MoreHorizontal, Trash2, MapPin, Calendar,
  Palmtree, ExternalLink, MessageCircle, ThumbsUp, PartyPopper,
  HandHeart, Lightbulb, HelpCircle, ChevronDown, ChevronUp,
  Plane, CheckSquare, Package, Sparkles, Clock, Sun, Check, Archive, Pencil
} from 'lucide-react';

const API_URL = window.location.origin;

// Reaction emoji mapping
const REACTIONS = {
  love: { emoji: '❤️', label: 'Love' },
  celebrate: { emoji: '🎉', label: 'Celebrate' },
  support: { emoji: '🤝🏾', label: 'Support' },
  insightful: { emoji: '💡', label: 'Insightful' },
  curious: { emoji: '🤔', label: 'Curious' },
  clap: { emoji: '👏🏾', label: 'Clapping' },
  strong: { emoji: '💪🏾', label: 'Strong' },
  raised: { emoji: '🙌🏾', label: 'Raised Hands' },
  pray: { emoji: '🙏🏾', label: 'Praying' },
  diamond: { emoji: '💎', label: 'Diamond' },
  fire: { emoji: '🔥', label: 'Fire' },
  sparkle: { emoji: '✨', label: 'Sparkle' }
};

const getProfileImageUrl = (profileImage) => {
  if (!profileImage) return null;
  if (profileImage.startsWith('http')) return profileImage;
  return `${API_URL}/api/${profileImage}`;
};

// Some legacy user records stored `location` as { latitude, longitude } (from a geolocation API).
// Coerce any non-string into a safe display string so React never crashes (error #31).
const formatLocation = (loc) => {
  if (!loc) return '';
  if (typeof loc === 'string') return loc;
  if (typeof loc === 'object') {
    if (typeof loc.city === 'string' || typeof loc.country === 'string') {
      return [loc.city, loc.state, loc.country].filter(Boolean).join(', ');
    }
    if (typeof loc.latitude === 'number' && typeof loc.longitude === 'number') {
      return `${loc.latitude.toFixed(2)}, ${loc.longitude.toFixed(2)}`;
    }
  }
  return '';
};

const CircleDetailPage = () => {
  const { circleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [circle, setCircle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [editingPostId, setEditingPostId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [posting, setPosting] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [newComments, setNewComments] = useState({});
  const [submittingComment, setSubmittingComment] = useState({});
  const [showReactionPicker, setShowReactionPicker] = useState(null);
  const [showCommentReactionPicker, setShowCommentReactionPicker] = useState(null);
  const [mentionSearch, setMentionSearch] = useState(null); // { postId, query, cursorPos }
  const [mentionResults, setMentionResults] = useState([]);
  const [retreatHubData, setRetreatHubData] = useState(null);
  const [activeTab, setActiveTab] = useState('community');
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, isPast: false });
  const [checklistUpdating, setChecklistUpdating] = useState({});

  useEffect(() => {
    fetchCircle();
  }, [circleId]);

  // Fetch retreat hub data when circle has retreat info
  useEffect(() => {
    if (circle?.retreat_info) {
      fetchRetreatHub();
    }
  }, [circle]);

  // Countdown timer for retreat
  useEffect(() => {
    if (!circle?.retreat_info?.dates) return;
    
    // Parse retreat start date (e.g., "June 26 - July 2, 2026")
    const dateString = circle.retreat_info.dates;
    const match = dateString.match(/(\w+)\s+(\d+)/);
    if (!match) return;
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const monthIndex = monthNames.findIndex(m => m.toLowerCase().startsWith(match[1].toLowerCase()));
    const day = parseInt(match[2]);
    const year = 2026; // Default year from retreat info
    
    if (monthIndex === -1) return;
    
    const retreatDate = new Date(year, monthIndex, day);
    
    const updateCountdown = () => {
      const now = new Date();
      const diff = retreatDate - now;
      
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true });
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown({ days, hours, minutes, seconds, isPast: false });
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, [circle?.retreat_info?.dates]);

  const fetchRetreatHub = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/retreat/hub`);
      setRetreatHubData(response.data);
    } catch (error) {
      console.error('Failed to fetch retreat hub:', error);
    }
  };

  const fetchCircle = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/circles/${circleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCircle(response.data);
    } catch (error) {
      console.error('Failed to fetch circle:', error);
      if (error.response?.status === 403) {
        toast.error('This is a private circle');
        navigate('/circles');
      } else if (error.response?.status === 404) {
        toast.error('Circle not found');
        navigate('/circles');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.trim()) {
      toast.error('Please enter some content');
      return;
    }

    setPosting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/circles/${circleId}/posts?content=${encodeURIComponent(newPost)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add new post to the list
      setCircle(prev => ({
        ...prev,
        recent_posts: [response.data.post, ...(prev.recent_posts || [])]
      }));
      setNewPost('');
      toast.success('Post created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setPosting(false);
    }
  };

  const handleSavePostEdit = async (postId) => {
    if (!editingContent.trim()) { toast.error('Post cannot be empty'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/circles/${circleId}/posts/${postId}?content=${encodeURIComponent(editingContent)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCircle(prev => ({
        ...prev,
        recent_posts: prev.recent_posts.map(p => p.id === postId ? { ...p, content: editingContent, edited_at: new Date().toISOString() } : p)
      }));
      setEditingPostId(null);
      toast.success('Post updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update post');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/circles/${circleId}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCircle(prev => ({
        ...prev,
        recent_posts: prev.recent_posts.filter(p => p.id !== postId)
      }));
      toast.success('Post deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  const handleJoin = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/circles/${circleId}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Joined circle!');
      fetchCircle();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    }
  };

  const handleLeave = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/circles/${circleId}/leave`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Left circle');
      navigate('/circles');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this circle? This cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/circles/${circleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Circle deleted');
      navigate('/circles');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleReaction = async (postId, reactionType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/circles/${circleId}/posts/${postId}/reactions?reaction_type=${reactionType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update the post's reactions in state
      setCircle(prev => ({
        ...prev,
        recent_posts: prev.recent_posts.map(post => 
          post.id === postId ? { ...post, reactions: response.data.reactions } : post
        )
      }));
      setShowReactionPicker(null);
    } catch (error) {
      toast.error('Failed to add reaction');
    }
  };

  const handleCommentReaction = async (postId, commentId, reactionType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/circles/${circleId}/posts/${postId}/comments/${commentId}/reactions?reaction_type=${reactionType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCircle(prev => ({
        ...prev,
        recent_posts: prev.recent_posts.map(post => {
          if (post.id !== postId) return post;
          return {
            ...post,
            comments: (post.comments || []).map(c =>
              c.id === commentId ? { ...c, reactions: response.data.reactions } : c
            )
          };
        })
      }));
      setShowCommentReactionPicker(null);
    } catch (error) {
      toast.error('Failed to react');
    }
  };

  const getCommentReactionCount = (reactions) => {
    if (!reactions) return 0;
    return Object.values(reactions).reduce((sum, arr) => sum + (arr?.length || 0), 0);
  };

  const getUserCommentReaction = (reactions) => {
    if (!reactions || !user) return null;
    for (const [type, users] of Object.entries(reactions)) {
      if (users?.includes(user.id)) return type;
    }
    return null;
  };

  // @mention helpers
  const handleCommentInputChange = (postId, value) => {
    setNewComments(prev => ({ ...prev, [postId]: value }));
    
    // Detect @mention
    const cursorPos = value.length;
    const textBeforeCursor = value.substring(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (atMatch) {
      const query = atMatch[1].toLowerCase();
      const members = circle?.members_details || [];
      const filtered = members
        .filter(m => m.id !== user?.id && m.full_name?.toLowerCase().includes(query))
        .slice(0, 5);
      setMentionSearch({ postId, query });
      setMentionResults(filtered);
    } else {
      setMentionSearch(null);
      setMentionResults([]);
    }
  };

  const insertMention = (postId, member) => {
    const currentText = newComments[postId] || '';
    const newText = currentText.replace(/@(\w*)$/, `@${member.full_name} `);
    setNewComments(prev => ({ ...prev, [postId]: newText }));
    setMentionSearch(null);
    setMentionResults([]);
  };

  const renderCommentContent = (content) => {
    if (!content) return content;
    const parts = content.split(/(@[\w\s]+?)(?=\s|$|@)/g);
    return parts.map((part, i) => {
      const members = circle?.members_details || [];
      const isMention = part.startsWith('@') && members.some(m => `@${m.full_name}` === part.trim());
      if (isMention) {
        return <span key={i} className="font-semibold" style={{ color: '#C06C54' }}>{part}</span>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  const handleAddComment = async (postId) => {
    const content = newComments[postId]?.trim();
    if (!content) return;

    setSubmittingComment(prev => ({ ...prev, [postId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/circles/${circleId}/posts/${postId}/comments?content=${encodeURIComponent(content)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add comment to the post
      setCircle(prev => ({
        ...prev,
        recent_posts: prev.recent_posts.map(post => 
          post.id === postId 
            ? { ...post, comments: [...(post.comments || []), response.data.comment] }
            : post
        )
      }));
      setNewComments(prev => ({ ...prev, [postId]: '' }));
      setExpandedComments(prev => ({ ...prev, [postId]: true }));
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(prev => ({ ...prev, [postId]: false }));
    }
  };

  const toggleComments = (postId) => {
    setExpandedComments(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  // Handler for toggling travel checklist items
  const handleChecklistToggle = async (itemId, currentChecked) => {
    const newChecked = !currentChecked;
    setChecklistUpdating(prev => ({ ...prev, [itemId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/retreat/checklist/${itemId}?checked=${newChecked}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setRetreatHubData(prev => ({
        ...prev,
        travel_checklist: prev.travel_checklist.map(item =>
          item.id === itemId ? { ...item, checked: newChecked } : item
        )
      }));
      
      // Show celebratory toast for completing required items
      const item = retreatHubData?.travel_checklist?.find(i => i.id === itemId);
      if (newChecked && item?.required) {
        toast.success(`Great job! "${item.item}" is ready!`);
      }
    } catch (error) {
      toast.error('Failed to update checklist');
    } finally {
      setChecklistUpdating(prev => ({ ...prev, [itemId]: false }));
    }
  };

  // Calculate checklist progress
  const getChecklistProgress = () => {
    if (!retreatHubData?.travel_checklist) return { checked: 0, total: 0, percent: 0 };
    const checked = retreatHubData.travel_checklist.filter(item => item.checked).length;
    const total = retreatHubData.travel_checklist.length;
    return { checked, total, percent: Math.round((checked / total) * 100) };
  };

  const getReactionCount = (reactions) => {
    if (!reactions) return 0;
    return Object.values(reactions).reduce((sum, users) => sum + (users?.length || 0), 0);
  };

  const getUserReaction = (reactions) => {
    if (!reactions || !user) return null;
    for (const [type, users] of Object.entries(reactions)) {
      if (users?.includes(user.id)) return type;
    }
    return null;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8">
          <div className="text-center py-12 text-gray-500">Loading circle...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!circle) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8">
          <div className="text-center py-12 text-gray-500">Circle not found</div>
        </div>
      </DashboardLayout>
    );
  }

  const isCreator = circle.created_by === user?.id;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isMember = circle.is_member || isAdmin;
  const hasRetreatInfo = circle.retreat_info;
  const isArchived = !!circle.is_archived;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="circle-detail-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/circles')}
          className="mb-4 text-gray-600 hover:text-forest"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Circles
        </Button>

        {/* Circle Header */}
        <Card className={`card-base mb-6 ${hasRetreatInfo ? 'border-teal-200 bg-teal-50/30' : ''}`}>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  {hasRetreatInfo && <Palmtree className="h-6 w-6 text-teal-600" />}
                  <h1 className="font-heading text-2xl font-bold text-forest">{circle.name}</h1>
                  {hasRetreatInfo ? (
                    <Badge className="bg-teal-600 text-white border-0">Retreat</Badge>
                  ) : circle.is_private ? (
                    <Badge variant="outline"><Lock className="h-3 w-3 mr-1" /> Private</Badge>
                  ) : (
                    <Badge variant="outline"><Globe className="h-3 w-3 mr-1" /> Public</Badge>
                  )}
                </div>
                <p className="text-gray-600 mb-4">{circle.description}</p>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    <span>{circle.member_count} members</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Crown className="h-4 w-4 text-terracotta" />
                    <span>Created by {circle.creator_name}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                {isMember ? (
                  <>
                    {isCreator ? (
                      <Button 
                        variant="outline" 
                        onClick={handleDelete}
                        className="text-red-500 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete Circle
                      </Button>
                    ) : !hasRetreatInfo && (
                      <Button variant="outline" onClick={handleLeave}>
                        Leave Circle
                      </Button>
                    )}
                  </>
                ) : (
                  <Button 
                    onClick={handleJoin}
                    className={hasRetreatInfo ? "bg-teal-600 hover:bg-teal-700 text-white" : "bg-terracotta hover:bg-terracotta/90 text-white"}
                  >
                    Join Circle
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Retreat Info Card (for Costa Rica VIP Circle) */}
        {hasRetreatInfo && isMember && (
          <Card className="card-base mb-6 border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-teal-500 flex items-center justify-center flex-shrink-0">
                  <Palmtree className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-lg font-semibold text-forest mb-1">
                    {circle.retreat_info.retreat_name}
                  </h3>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4 text-teal-600" />
                      {circle.retreat_info.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4 text-teal-600" />
                      {circle.retreat_info.dates}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">{circle.retreat_info.description}</p>
                  <div className="flex flex-wrap gap-2">
                    <Link to={circle.retreat_info.welcome_page_url}>
                      <Button className="bg-teal-600 hover:bg-teal-700 text-white">
                        <ExternalLink className="h-4 w-4 mr-2" /> Welcome Page
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      className="border-teal-300 text-teal-700 hover:bg-teal-50"
                      onClick={() => setActiveTab('itinerary')}
                    >
                      <Calendar className="h-4 w-4 mr-2" /> Itinerary
                    </Button>
                    <Button 
                      variant="outline" 
                      className="border-teal-300 text-teal-700 hover:bg-teal-50"
                      onClick={() => setActiveTab('prepare')}
                    >
                      <Package className="h-4 w-4 mr-2" /> Prepare
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Countdown Timer Widget - Only for Retreat Circles */}
        {hasRetreatInfo && isMember && !countdown.isPast && (
          <Card className="card-base mb-6 overflow-hidden" data-testid="retreat-countdown">
            <div className="bg-gradient-to-r from-teal-600 via-emerald-600 to-teal-700 p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                    <Sun className="h-6 w-6 text-white animate-pulse" />
                  </div>
                  <div>
                    <h3 className="text-white font-heading text-lg font-semibold">
                      Costa Rica Awaits!
                    </h3>
                    <p className="text-white/80 text-sm">
                      Your transformation journey begins in...
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-3 sm:gap-4">
                  <div className="text-center">
                    <div className="bg-white/20 backdrop-blur rounded-lg px-3 py-2 min-w-[60px] sm:min-w-[70px]">
                      <span className="text-2xl sm:text-3xl font-bold text-white">{countdown.days}</span>
                    </div>
                    <span className="text-xs text-white/70 mt-1 block">Days</span>
                  </div>
                  <div className="text-center">
                    <div className="bg-white/20 backdrop-blur rounded-lg px-3 py-2 min-w-[60px] sm:min-w-[70px]">
                      <span className="text-2xl sm:text-3xl font-bold text-white">{String(countdown.hours).padStart(2, '0')}</span>
                    </div>
                    <span className="text-xs text-white/70 mt-1 block">Hours</span>
                  </div>
                  <div className="text-center">
                    <div className="bg-white/20 backdrop-blur rounded-lg px-3 py-2 min-w-[60px] sm:min-w-[70px]">
                      <span className="text-2xl sm:text-3xl font-bold text-white">{String(countdown.minutes).padStart(2, '0')}</span>
                    </div>
                    <span className="text-xs text-white/70 mt-1 block">Mins</span>
                  </div>
                  <div className="text-center hidden sm:block">
                    <div className="bg-white/20 backdrop-blur rounded-lg px-3 py-2 min-w-[70px]">
                      <span className="text-2xl sm:text-3xl font-bold text-white">{String(countdown.seconds).padStart(2, '0')}</span>
                    </div>
                    <span className="text-xs text-white/70 mt-1 block">Secs</span>
                  </div>
                </div>
              </div>
              
              {/* Motivational message based on days remaining */}
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-white/90 text-sm text-center italic">
                  {countdown.days > 30 
                    ? "Start preparing your heart and soul for this transformative experience."
                    : countdown.days > 7 
                    ? "The magic is almost here! Time to finalize your packing and intentions."
                    : countdown.days > 1
                    ? "Just days away! Your sisters are getting excited to meet you."
                    : "Tomorrow is the day! Get ready to exhale and soar."
                  }
                </p>
              </div>
              
              {/* Preparation Readiness Badge */}
              {retreatHubData?.travel_checklist?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/20" data-testid="readiness-badge">
                  {(() => {
                    const progress = getChecklistProgress();
                    const requiredItems = retreatHubData.travel_checklist.filter(i => i.required);
                    const requiredChecked = requiredItems.filter(i => i.checked).length;
                    const allRequiredDone = requiredChecked === requiredItems.length;
                    
                    return (
                      <div 
                        className="flex items-center justify-between bg-white/10 backdrop-blur rounded-lg p-3 cursor-pointer hover:bg-white/20 transition-colors"
                        onClick={() => setActiveTab('prepare')}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                            progress.percent === 100 
                              ? 'bg-emerald-400 text-white' 
                              : allRequiredDone 
                                ? 'bg-teal-400 text-white'
                                : progress.percent >= 50 
                                  ? 'bg-amber-400 text-white'
                                  : 'bg-white/30 text-white'
                          }`}>
                            {progress.percent === 100 ? (
                              <Sparkles className="h-5 w-5" />
                            ) : (
                              <span className="text-sm font-bold">{progress.percent}%</span>
                            )}
                          </div>
                          <div>
                            <p className="text-white font-medium text-sm">
                              {progress.percent === 100 
                                ? "You're All Set for Costa Rica!" 
                                : allRequiredDone
                                  ? "Essentials Ready!"
                                  : progress.percent >= 50
                                    ? "Making Great Progress!"
                                    : "Start Your Checklist"
                              }
                            </p>
                            <p className="text-white/70 text-xs">
                              {progress.checked}/{progress.total} items • {requiredChecked}/{requiredItems.length} required
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {progress.percent === 100 ? (
                            <Badge className="bg-emerald-400 text-white border-0">
                              <Check className="h-3 w-3 mr-1" /> Complete
                            </Badge>
                          ) : allRequiredDone ? (
                            <Badge className="bg-teal-400 text-white border-0">
                              Essentials Done
                            </Badge>
                          ) : (
                            <Badge className="bg-white/20 text-white border-0">
                              {progress.total - progress.checked} remaining
                            </Badge>
                          )}
                          <ChevronDown className="h-4 w-4 text-white/70" />
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Archived Banner */}
        {isArchived && (
          <div className="mb-6 rounded-xl border-2 border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="circle-archived-banner">
            <Archive className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-800">This circle has been archived — the retreat has ended 🌴</p>
              <p className="text-sm text-amber-700 mt-1">You can still revisit all the posts and memories below, but new posts and comments are closed.</p>
            </div>
          </div>
        )}

        {/* Retreat Hub Tabs - Only for Retreat Circles */}
        {hasRetreatInfo && isMember && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="grid w-full grid-cols-4 mb-4">
              <TabsTrigger value="community" className="gap-1 text-xs sm:text-sm">
                <MessageCircle className="h-4 w-4" />
                <span className="hidden sm:inline">Community</span>
              </TabsTrigger>
              <TabsTrigger value="itinerary" className="gap-1 text-xs sm:text-sm">
                <Calendar className="h-4 w-4" />
                <span className="hidden sm:inline">Itinerary</span>
              </TabsTrigger>
              <TabsTrigger value="prepare" className="gap-1 text-xs sm:text-sm">
                <Package className="h-4 w-4" />
                <span className="hidden sm:inline">Prepare</span>
              </TabsTrigger>
              <TabsTrigger value="integration" className="gap-1 text-xs sm:text-sm">
                <Sparkles className="h-4 w-4" />
                <span className="hidden sm:inline">Integration</span>
              </TabsTrigger>
            </TabsList>

            {/* Itinerary Tab */}
            <TabsContent value="itinerary">
              <Card className="card-base border-teal-200" data-testid="itinerary-tab-content">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-teal-600" />
                    Retreat Itinerary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {retreatHubData?.itinerary?.length > 0 ? (
                    <div className="space-y-4" data-testid="itinerary-list">
                      {retreatHubData.itinerary.map((day, index) => (
                        <div key={index} className="border-l-4 border-teal-500 pl-4 py-3 bg-teal-50/50 rounded-r-lg" data-testid={`itinerary-day-${day.day}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-teal-600 bg-teal-100 px-2 py-0.5 rounded">Day {day.day}</span>
                            <span className="text-xs text-gray-500">{day.date}</span>
                          </div>
                          <h4 className="font-semibold text-forest text-lg">{day.title}</h4>
                          <p className="text-gray-600 text-sm mt-1">{day.description}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-600">Itinerary details coming soon!</p>
                      <p className="text-sm text-gray-500 mt-2">Check the Welcome Page for more information.</p>
                      <Link to={circle.retreat_info.welcome_page_url} className="mt-4 inline-block">
                        <Button variant="outline" className="border-teal-300 text-teal-700">
                          <ExternalLink className="h-4 w-4 mr-2" /> View Welcome Page
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Prepare Tab */}
            <TabsContent value="prepare">
              <div className="grid md:grid-cols-2 gap-6" data-testid="prepare-tab-content">
                <Card className="card-base border-teal-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                      <Plane className="h-5 w-5 text-teal-600" />
                      Travel Checklist
                    </CardTitle>
                    {/* Progress Bar */}
                    {retreatHubData?.travel_checklist?.length > 0 && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600">Your Progress</span>
                          <span className="font-medium text-teal-700">
                            {getChecklistProgress().checked}/{getChecklistProgress().total} completed
                          </span>
                        </div>
                        <Progress 
                          value={getChecklistProgress().percent} 
                          className="h-2 bg-gray-100"
                        />
                        {getChecklistProgress().percent === 100 && (
                          <p className="text-sm text-emerald-600 font-medium mt-2 flex items-center gap-1">
                            <Sparkles className="h-4 w-4" /> You're all set for Costa Rica!
                          </p>
                        )}
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    {retreatHubData?.travel_checklist?.length > 0 ? (
                      <div className="space-y-2" data-testid="travel-checklist">
                        {retreatHubData.travel_checklist.map((checklistItem, index) => (
                          <div 
                            key={checklistItem.id || index} 
                            className={`flex items-start gap-3 p-3 rounded-lg transition-all cursor-pointer ${
                              checklistItem.checked 
                                ? 'bg-emerald-50 border border-emerald-200' 
                                : checklistItem.required 
                                  ? 'bg-amber-50 border border-amber-200 hover:bg-amber-100/50' 
                                  : 'bg-teal-50 hover:bg-teal-100/50'
                            }`}
                            data-testid={`checklist-item-${checklistItem.id || index}`}
                            onClick={() => !checklistUpdating[checklistItem.id] && handleChecklistToggle(checklistItem.id, checklistItem.checked)}
                          >
                            <Checkbox
                              checked={checklistItem.checked}
                              disabled={checklistUpdating[checklistItem.id]}
                              onCheckedChange={() => handleChecklistToggle(checklistItem.id, checklistItem.checked)}
                              className={`mt-0.5 ${
                                checklistItem.checked 
                                  ? 'border-emerald-500 data-[state=checked]:bg-emerald-500' 
                                  : checklistItem.required 
                                    ? 'border-amber-500' 
                                    : 'border-teal-500'
                              }`}
                              data-testid={`checkbox-${checklistItem.id}`}
                            />
                            <div className="flex-1">
                              <span className={`text-sm ${checklistItem.checked ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
                                {checklistItem.item}
                              </span>
                              {checklistItem.required && !checklistItem.checked && (
                                <span className="ml-2 text-xs text-amber-600 font-medium">(Required)</span>
                              )}
                              {checklistItem.checked && (
                                <span className="ml-2 text-xs text-emerald-600 font-medium flex items-center gap-1 inline-flex">
                                  <Check className="h-3 w-3" /> Done
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <Plane className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-600 text-sm">Travel checklist coming soon!</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="card-base border-teal-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                      <Package className="h-5 w-5 text-teal-600" />
                      Packing List
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {retreatHubData?.packing_list?.length > 0 ? (
                      <div className="space-y-2" data-testid="packing-list">
                        {retreatHubData.packing_list.map((packingItem, index) => (
                          <div key={index} className="flex items-center gap-2 p-2 bg-teal-50 rounded-lg">
                            <CheckSquare className="h-4 w-4 text-teal-600 flex-shrink-0" />
                            <span className="text-sm text-gray-700">
                              {typeof packingItem === 'string' ? packingItem : packingItem.item}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <Package className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-600 text-sm">Packing list coming soon!</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Integration Tab */}
            <TabsContent value="integration">
              <Card className="card-base border-teal-200">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-teal-600" />
                    30-Day Integration Track
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl p-6 mb-6">
                    <p className="text-white/90 mb-4">
                      Your transformation doesn't end when you leave Costa Rica. 
                      This 30-day integration track helps you carry the magic home.
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-3xl font-bold">{retreatHubData?.integration_track?.current_day || 0}</p>
                        <p className="text-xs text-white/70">Current Day</p>
                      </div>
                      <div className="flex-1 h-3 bg-white/20 rounded-full">
                        <div 
                          className="h-3 bg-white rounded-full transition-all"
                          style={{ width: `${((retreatHubData?.integration_track?.current_day || 0) / 30) * 100}%` }}
                        />
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold">30</p>
                        <p className="text-xs text-white/70">Total Days</p>
                      </div>
                    </div>
                  </div>
                  <div className="text-center py-4">
                    <Sparkles className="h-10 w-10 text-teal-300 mx-auto mb-3" />
                    <p className="text-gray-600 text-sm">
                      Integration prompts and resources will appear here during your 30-day journey after the retreat.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Community Tab - Shows the rest of the page content */}
            <TabsContent value="community">
              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="retreat-community-tab">
                {/* Left Column - Posts */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Post Creation */}
                  {isMember && !isArchived && (
                    <Card className="card-base">
                      <CardContent className="p-4">
                        <div className="flex gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={user?.avatar} />
                            <AvatarFallback className="bg-teal-500 text-white">
                              {user?.full_name?.charAt(0) || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <Textarea
                              value={newPost}
                              onChange={(e) => setNewPost(e.target.value)}
                              placeholder="Share with your retreat sisters..."
                              className="input-base min-h-[80px] mb-2"
                              data-testid="retreat-post-input"
                            />
                            <div className="flex justify-end">
                              <Button 
                                onClick={handleCreatePost}
                                disabled={posting || !newPost.trim()}
                                className="bg-teal-600 hover:bg-teal-700 text-white"
                                data-testid="retreat-post-submit"
                              >
                                {posting ? 'Posting...' : <><Send className="h-4 w-4 mr-2" /> Post</>}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Posts */}
                  <div className="space-y-4">
                    <h2 className="font-heading text-lg font-semibold text-forest flex items-center gap-2">
                      <MessageCircle className="h-5 w-5 text-teal-600" />
                      Circle Discussion
                    </h2>
                    {circle.recent_posts?.length > 0 ? (
                      circle.recent_posts.map(post => (
                        <Card key={post.id} className="card-base" data-testid={`retreat-post-${post.id}`}>
                          <CardContent className="p-4">
                            {/* Post Header & Content */}
                            <div className="flex gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarImage src={getProfileImageUrl(post.author_avatar)} />
                                <AvatarFallback className="bg-teal-500 text-white">
                                  {post.author_name?.charAt(0) || 'U'}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-forest">{post.author_name}</span>
                                  <span className="text-xs text-gray-400">
                                    {new Date(post.created_at).toLocaleDateString()}
                                    {post.edited_at && <span className="italic"> · edited</span>}
                                  </span>
                                  <span className="ml-auto flex items-center gap-1.5">
                                    {post.author_id === user?.id && editingPostId !== post.id && (
                                      <button onClick={() => { setEditingPostId(post.id); setEditingContent(post.content); }} className="text-gray-300 hover:text-forest" data-testid={`circle-post-edit-${post.id}`}>
                                        <Pencil className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                    {(post.author_id === user?.id || isAdmin) && (
                                      <button onClick={() => handleDeletePost(post.id)} className="text-gray-300 hover:text-red-400" data-testid={`circle-post-delete-${post.id}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                  </span>
                                </div>
                                {editingPostId === post.id ? (
                                  <div className="space-y-2">
                                    <Textarea value={editingContent} onChange={(e) => setEditingContent(e.target.value)} rows={3} data-testid={`circle-post-edit-input-${post.id}`} />
                                    <div className="flex gap-2 justify-end">
                                      <Button size="sm" variant="ghost" onClick={() => setEditingPostId(null)}>Cancel</Button>
                                      <Button size="sm" className="bg-forest text-white" onClick={() => handleSavePostEdit(post.id)} data-testid={`circle-post-edit-save-${post.id}`}>Save</Button>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-gray-700 whitespace-pre-wrap">{post.content}</p>
                                )}
                              </div>
                            </div>

                            {/* Reactions Display */}
                            {getReactionCount(post.reactions) > 0 && (
                              <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
                                <div className="flex -space-x-1">
                                  {Object.entries(post.reactions || {}).map(([type, users]) => (
                                    users?.length > 0 && (
                                      <span key={type} className="text-base" title={`${users.length} ${REACTIONS[type]?.label}`}>
                                        {REACTIONS[type]?.emoji}
                                      </span>
                                    )
                                  ))}
                                </div>
                                <span>{getReactionCount(post.reactions)} {getReactionCount(post.reactions) === 1 ? 'reaction' : 'reactions'}</span>
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div className="mt-3 pt-3 border-t flex items-center gap-2">
                              {/* Reaction Button */}
                              {!isArchived && (
                              <div className="relative">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`text-gray-500 hover:text-rose-500 ${getUserReaction(post.reactions) ? 'text-rose-500' : ''}`}
                                  onClick={() => setShowReactionPicker(showReactionPicker === post.id ? null : post.id)}
                                  data-testid={`react-btn-${post.id}`}
                                >
                                  {getUserReaction(post.reactions) 
                                    ? REACTIONS[getUserReaction(post.reactions)]?.emoji
                                    : <Heart className="h-4 w-4 mr-1" />
                                  }
                                  React
                                </Button>
                                
                                {/* Reaction Picker Dropdown */}
                                {showReactionPicker === post.id && (
                                  <div className="absolute left-0 top-full mt-1 bg-white rounded-full shadow-lg border p-2 flex gap-1 z-10">
                                    {Object.entries(REACTIONS).map(([type, { emoji, label }]) => (
                                      <button
                                        key={type}
                                        onClick={() => handleReaction(post.id, type)}
                                        className={`text-xl hover:scale-125 transition-transform p-1 rounded ${
                                          getUserReaction(post.reactions) === type ? 'bg-rose-100' : ''
                                        }`}
                                        title={label}
                                      >
                                        {emoji}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                              )}

                              {/* Comment Button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-gray-500 hover:text-teal-600"
                                onClick={() => toggleComments(post.id)}
                                data-testid={`comments-toggle-${post.id}`}
                              >
                                <MessageSquare className="h-4 w-4 mr-1" />
                                {post.comments?.length || 0} {post.comments?.length === 1 ? 'Comment' : 'Comments'}
                                {expandedComments[post.id] ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
                              </Button>
                            </div>

                            {/* Comments Section */}
                            {expandedComments[post.id] && (
                              <div className="mt-3 pt-3 border-t space-y-3" data-testid={`comments-section-${post.id}`}>
                                {/* Existing Comments */}
                                {post.comments?.length > 0 && (
                                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                                    {post.comments.map((comment, idx) => (
                                      <div key={comment.id || idx} className="flex gap-2 pl-2">
                                        <Avatar className="h-8 w-8">
                                          <AvatarImage src={getProfileImageUrl(comment.author_avatar)} />
                                          <AvatarFallback className="bg-gray-200 text-gray-600 text-xs">
                                            {comment.author_name?.charAt(0) || 'U'}
                                          </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1">
                                          <div className="bg-teal-50 rounded-lg p-2">
                                            <div className="flex items-center gap-2">
                                              <span className="font-medium text-sm text-forest">{comment.author_name}</span>
                                              <span className="text-xs text-gray-400">
                                                {new Date(comment.created_at).toLocaleDateString()}
                                              </span>
                                            </div>
                                            <p className="text-sm text-gray-700">{renderCommentContent(comment.content)}</p>
                                          </div>
                                          {/* Comment Reactions */}
                                          <div className="flex items-center gap-1 mt-1 ml-1">
                                            {getCommentReactionCount(comment.reactions) > 0 && (
                                              <span className="text-xs text-gray-400 mr-1">
                                                {Object.entries(comment.reactions || {}).map(([type, users]) =>
                                                  users?.length > 0 ? `${REACTIONS[type]?.emoji || ''}${users.length > 1 ? users.length : ''}` : ''
                                                ).filter(Boolean).join(' ')}
                                              </span>
                                            )}
                                            <div className="relative">
                                              {!isArchived && (
                                              <button
                                                className={`text-xs px-1.5 py-0.5 rounded-full transition-colors ${getUserCommentReaction(comment.reactions) ? 'text-rose-500' : 'text-gray-400 hover:text-gray-600'}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setShowCommentReactionPicker(showCommentReactionPicker === comment.id ? null : comment.id);
                                                }}
                                                data-testid={`comment-react-btn-${comment.id}`}
                                              >
                                                {getUserCommentReaction(comment.reactions)
                                                  ? REACTIONS[getUserCommentReaction(comment.reactions)]?.emoji
                                                  : 'React'}
                                              </button>
                                              )}
                                              {showCommentReactionPicker === comment.id && (
                                                <div className="absolute bottom-full left-0 mb-1 flex gap-1 bg-white rounded-full shadow-lg border px-2 py-1 z-50">
                                                  {Object.entries(REACTIONS).map(([type, { emoji, label }]) => (
                                                    <button
                                                      key={type}
                                                      onClick={(e) => { e.stopPropagation(); handleCommentReaction(post.id, comment.id, type); }}
                                                      title={label}
                                                      className="hover:scale-125 transition-transform text-base"
                                                      data-testid={`comment-reaction-${type}-${comment.id}`}
                                                    >{emoji}</button>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Add Comment Input */}
                                {isMember && !isArchived && (
                                  <div className="flex gap-2 pt-2">
                                    <Avatar className="h-8 w-8">
                                      <AvatarImage src={getProfileImageUrl(user?.profile_image)} />
                                      <AvatarFallback className="bg-teal-500 text-white text-xs">
                                        {user?.full_name?.charAt(0) || 'U'}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 relative">
                                      <div className="flex gap-2">
                                        <input
                                          type="text"
                                          placeholder="Write a reply... Use @ to mention"
                                          value={newComments[post.id] || ''}
                                          onChange={(e) => handleCommentInputChange(post.id, e.target.value)}
                                          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAddComment(post.id)}
                                          className="flex-1 text-sm border rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                                          data-testid={`comment-input-${post.id}`}
                                        />
                                        <Button
                                          size="sm"
                                          onClick={() => handleAddComment(post.id)}
                                          disabled={!newComments[post.id]?.trim() || submittingComment[post.id]}
                                          className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                                          data-testid={`comment-submit-${post.id}`}
                                        >
                                          <Send className="h-4 w-4" />
                                        </Button>
                                      </div>
                                      {/* @Mention Dropdown */}
                                      {mentionSearch?.postId === post.id && mentionResults.length > 0 && (
                                        <div className="absolute bottom-full left-0 mb-1 w-64 bg-white border rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto" data-testid="mention-dropdown">
                                          {mentionResults.map(member => (
                                            <button
                                              key={member.id}
                                              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-teal-50 text-left transition-colors"
                                              onClick={() => insertMention(post.id, member)}
                                              data-testid={`mention-${member.id}`}
                                            >
                                              <Avatar className="h-6 w-6">
                                                <AvatarImage src={getProfileImageUrl(member.profile_image)} />
                                                <AvatarFallback className="bg-gray-200 text-gray-600 text-[10px]">
                                                  {member.full_name?.charAt(0) || '?'}
                                                </AvatarFallback>
                                              </Avatar>
                                              <span className="text-sm text-forest truncate">{member.full_name}</span>
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <Card className="card-base">
                        <CardContent className="py-12 text-center">
                          <MessageCircle className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                          <p className="text-gray-500">No posts yet. Start the conversation!</p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>

                {/* Right Column - Members */}
                <div className="lg:col-span-1">
                  <Card className="card-base sticky top-6 border-teal-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-heading text-teal-700 flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Retreat Sisters
                        <Badge variant="outline" className="ml-auto text-xs">{circle.member_count}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {circle.members_details?.length > 0 ? (
                        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                          {circle.members_details.map((member, idx) => (
                            <div key={member.id || idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                              <Avatar className="h-10 w-10">
                                <AvatarImage src={getProfileImageUrl(member.profile_image)} />
                                <AvatarFallback className="bg-teal-100 text-teal-700">
                                  {member.full_name?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-forest truncate flex items-center gap-1">
                                  {member.full_name}
                                  {member.is_vip && (
                                    <Crown className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" title="VIP Member" />
                                  )}
                                </p>
                                {formatLocation(member.location) && (
                                  <p className="text-xs text-gray-500 flex items-center gap-1 truncate">
                                    <MapPin className="h-3 w-3 flex-shrink-0" />
                                    {formatLocation(member.location)}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center py-4 text-gray-500 text-sm">Loading members...</p>
                      )}
                      
                      {/* VIP Key Legend */}
                      <div className="mt-4 p-2 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-2 text-amber-700 text-xs">
                          <Crown className="h-3.5 w-3.5" />
                          <span>= VIP Experience</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Non-Retreat Circle Layout */}
        {(!hasRetreatInfo || !isMember) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Posts */}
          <div className="lg:col-span-2 space-y-6">
            {/* Post Creation (Members Only) */}
            {isMember && !isArchived && (
              <Card className="card-base">
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user?.avatar} />
                      <AvatarFallback className={`${hasRetreatInfo ? 'bg-teal-500' : 'bg-terracotta'} text-white`}>
                        {user?.full_name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <Textarea
                        value={newPost}
                        onChange={(e) => setNewPost(e.target.value)}
                        placeholder={hasRetreatInfo ? "Share with your retreat sisters..." : "Share something with the circle..."}
                        className="input-base min-h-[80px] mb-2"
                      />
                      <div className="flex justify-end">
                        <Button 
                          onClick={handleCreatePost}
                          disabled={posting || !newPost.trim()}
                          className={hasRetreatInfo ? "bg-teal-600 hover:bg-teal-700 text-white" : "bg-forest hover:bg-forest/90 text-white"}
                        >
                          {posting ? 'Posting...' : (
                            <>
                              <Send className="h-4 w-4 mr-2" /> Post
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Posts Feed */}
            <div className="space-y-4">
              <h2 className="font-heading text-lg font-semibold text-forest flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Circle Discussion
              </h2>
              
              {circle.recent_posts?.length > 0 ? (
                circle.recent_posts.map(post => (
                  <Card key={post.id} className="card-base">
                    <CardContent className="p-4">
                      {/* Post Header & Content */}
                      <div className="flex gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={getProfileImageUrl(post.author_avatar)} />
                          <AvatarFallback className="bg-forest text-white">
                            {post.author_name?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-forest">{post.author_name}</span>
                            <span className="text-xs text-gray-400">
                              {new Date(post.created_at).toLocaleDateString()}
                              {post.edited_at && <span className="italic"> · edited</span>}
                            </span>
                            <span className="ml-auto flex items-center gap-1.5">
                              {post.author_id === user?.id && editingPostId !== post.id && (
                                <button onClick={() => { setEditingPostId(post.id); setEditingContent(post.content); }} className="text-gray-300 hover:text-forest" data-testid={`circle-post-edit2-${post.id}`}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              )}
                              {(post.author_id === user?.id || isAdmin) && (
                                <button onClick={() => handleDeletePost(post.id)} className="text-gray-300 hover:text-red-400" data-testid={`circle-post-delete2-${post.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </span>
                          </div>
                          {editingPostId === post.id ? (
                            <div className="space-y-2">
                              <Textarea value={editingContent} onChange={(e) => setEditingContent(e.target.value)} rows={3} data-testid={`circle-post-edit2-input-${post.id}`} />
                              <div className="flex gap-2 justify-end">
                                <Button size="sm" variant="ghost" onClick={() => setEditingPostId(null)}>Cancel</Button>
                                <Button size="sm" className="bg-forest text-white" onClick={() => handleSavePostEdit(post.id)} data-testid={`circle-post-edit2-save-${post.id}`}>Save</Button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-gray-700 whitespace-pre-wrap">{post.content}</p>
                          )}
                        </div>
                      </div>

                      {/* Reactions Display */}
                      {getReactionCount(post.reactions) > 0 && (
                        <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
                          <div className="flex -space-x-1">
                            {Object.entries(post.reactions || {}).map(([type, users]) => (
                              users?.length > 0 && (
                                <span key={type} className="text-base" title={`${users.length} ${REACTIONS[type]?.label}`}>
                                  {REACTIONS[type]?.emoji}
                                </span>
                              )
                            ))}
                          </div>
                          <span>{getReactionCount(post.reactions)} {getReactionCount(post.reactions) === 1 ? 'reaction' : 'reactions'}</span>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="mt-3 pt-3 border-t flex items-center gap-2">
                        {/* Reaction Button */}
                        {!isArchived && (
                        <div className="relative">
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`text-gray-500 hover:text-rose-500 ${getUserReaction(post.reactions) ? 'text-rose-500' : ''}`}
                            onClick={() => setShowReactionPicker(showReactionPicker === post.id ? null : post.id)}
                          >
                            {getUserReaction(post.reactions) 
                              ? REACTIONS[getUserReaction(post.reactions)]?.emoji
                              : <Heart className="h-4 w-4 mr-1" />
                            }
                            React
                          </Button>
                          
                          {/* Reaction Picker Dropdown */}
                          {showReactionPicker === post.id && (
                            <div className="absolute left-0 top-full mt-1 bg-white rounded-full shadow-lg border p-2 flex gap-1 z-10">
                              {Object.entries(REACTIONS).map(([type, { emoji, label }]) => (
                                <button
                                  key={type}
                                  onClick={() => handleReaction(post.id, type)}
                                  className={`text-xl hover:scale-125 transition-transform p-1 rounded ${
                                    getUserReaction(post.reactions) === type ? 'bg-rose-100' : ''
                                  }`}
                                  title={label}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        )}

                        {/* Comment Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-500 hover:text-forest"
                          onClick={() => toggleComments(post.id)}
                        >
                          <MessageSquare className="h-4 w-4 mr-1" />
                          {post.comments?.length || 0} {post.comments?.length === 1 ? 'Comment' : 'Comments'}
                          {expandedComments[post.id] ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
                        </Button>
                      </div>

                      {/* Comments Section */}
                      {expandedComments[post.id] && (
                        <div className="mt-3 pt-3 border-t space-y-3">
                          {/* Existing Comments */}
                          {post.comments?.length > 0 && (
                            <div className="space-y-3 max-h-[300px] overflow-y-auto">
                              {post.comments.map((comment, idx) => (
                                <div key={comment.id || idx} className="flex gap-2 pl-2">
                                  <Avatar className="h-8 w-8">
                                    <AvatarImage src={getProfileImageUrl(comment.author_avatar)} />
                                    <AvatarFallback className="bg-gray-200 text-gray-600 text-xs">
                                      {comment.author_name?.charAt(0) || 'U'}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="flex-1 bg-gray-50 rounded-lg p-2">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-sm text-forest">{comment.author_name}</span>
                                      <span className="text-xs text-gray-400">
                                        {new Date(comment.created_at).toLocaleDateString()}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-700">{comment.content}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Add Comment Input */}
                          {isMember && !isArchived && (
                            <div className="flex gap-2 pt-2">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={getProfileImageUrl(user?.profile_image)} />
                                <AvatarFallback className="bg-forest text-white text-xs">
                                  {user?.full_name?.charAt(0) || 'U'}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 flex gap-2">
                                <input
                                  type="text"
                                  placeholder="Write a comment..."
                                  value={newComments[post.id] || ''}
                                  onChange={(e) => setNewComments(prev => ({ ...prev, [post.id]: e.target.value }))}
                                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAddComment(post.id)}
                                  className="flex-1 text-sm border rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-forest/20"
                                />
                                <Button
                                  size="sm"
                                  onClick={() => handleAddComment(post.id)}
                                  disabled={!newComments[post.id]?.trim() || submittingComment[post.id]}
                                  className="bg-forest hover:bg-forest/90 text-white rounded-full"
                                >
                                  <Send className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="card-base">
                  <CardContent className="py-12 text-center">
                    <MessageSquare className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">
                      {isMember ? 'No posts yet. Start the conversation!' : 'Join to see and create posts'}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Right Column - Members Sidebar */}
          <div className="lg:col-span-1">
            <Card className={`card-base sticky top-6 ${hasRetreatInfo ? 'border-teal-200' : ''}`}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-lg font-heading ${hasRetreatInfo ? 'text-teal-700' : 'text-forest'} flex items-center gap-2`}>
                  <Users className="h-5 w-5" />
                  {hasRetreatInfo ? 'Retreat Sisters' : 'Circle Members'}
                  <Badge variant="outline" className="ml-auto text-xs">
                    {circle.member_count}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {circle.members_details?.length > 0 ? (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                    {circle.members_details.map((member, idx) => (
                      <div key={member.id || idx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={getProfileImageUrl(member.profile_image)} />
                          <AvatarFallback className={`${hasRetreatInfo ? 'bg-teal-100 text-teal-700' : 'bg-purple-100 text-purple-600'}`}>
                            {member.full_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-forest truncate flex items-center gap-1">
                            {member.full_name}
                            {member.is_vip && (
                              <Crown className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" title="VIP Member" />
                            )}
                          </p>
                          {formatLocation(member.location) && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 truncate">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              {formatLocation(member.location)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500 text-sm">
                    {isMember ? 'Member list loading...' : 'Join to see members'}
                  </div>
                )}

                {/* Retreat Circle Info */}
                {hasRetreatInfo && (
                  <div className="mt-6 space-y-3">
                    {/* VIP Key Legend */}
                    <div className="p-2 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 text-amber-700 text-xs">
                        <Crown className="h-3.5 w-3.5" />
                        <span>= VIP Experience</span>
                      </div>
                    </div>
                    
                    {/* Retreat Info */}
                    <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                      <div className="flex items-center gap-2 text-teal-700">
                        <Palmtree className="h-4 w-4" />
                        <span className="text-sm font-medium">Retreat Community</span>
                      </div>
                      <p className="text-xs text-teal-600 mt-1">
                        Connect with your fellow retreat sisters
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CircleDetailPage;
